import React, { useEffect, useMemo, useState } from "react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import HeaderPanel from "../layout/HeaderPanel";
import {
  moveTreeViewItem,
  processTreeViewItems,
  TreeViewDragAnalyzer,
  TreeViewDragClue,
  TreeViewExpandChangeEvent,
  TreeViewItemDragEndEvent,
  TreeViewItemDragOverEvent,
} from "@progress/kendo-react-treeview";
import FooterPanel from "../layout/FooterPanel";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { TreeViewDataItem } from "../cpm/DocumentPartTree";
import { StructureDocumentPart } from "../../api/models/structure-document-part.ts";
import { fetchDocumentPartNodeWithPermissions } from "../../api/document/DocumentsPart";
import { DocumentRoot } from "../../api/models/document-root";
import { IContextualMenuProps } from "@fluentui/react";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import NotificationManager from "../../common/notification/NotificationManager";
import { getFullTree } from "./DBMapping";
import { ElaborationStatusItem } from "../../redux/feature/object/remote-elaborations-slice";
import { CustomTreeView } from "../cpm/CustomTreeView";
import { mapFormToDataItem } from "../cpm/DocumentPartTreeUtils";

export interface ManageStructureState {
  manageTreeItems: TreeViewDataItem[];
  selectState: string[];
  selectedId?: string;
  expand: string[];
  count: number;
}

interface ManageStructureProps {
  expandIcons?: boolean;
  getIconButtonMenuProps?: (item: any, itemHierarchicalIndex: string) => IContextualMenuProps;
  selectedTreeItem: FlatPathTreeItem;
  changeDocumentPartStructure?: () => Promise<void>;
  updateTreeItems: (treeItems: any, modifiedFields: boolean) => void;
  modifiedFields: boolean;
  onBackButtonClick: () => void;
  elaborationItem?: ElaborationStatusItem;
}

const initialState = {
  manageTreeItems: [],
  selectState: [],
  expand: [],
  count: 0,
};

export const ManageStructure: React.FunctionComponent<ManageStructureProps> = (props) => {
  const { selectedTreeItem, getIconButtonMenuProps } = props;
  const expandIcons: boolean = props.expandIcons !== false;
  const dragClue = React.useRef<any>();
  const dragOverCnt = React.useRef<number>(0);
  const isDragDrop = React.useRef<boolean>(false);
  const SEPARATOR = "_";

  const rootItem = useMemo<TreeViewDataItem[]>(() => {
    return [
      {
        text: selectedTreeItem.document.name,
        key: selectedTreeItem.document.id,
        hasChildren: selectedTreeItem.document.hasChildren,
        nodeType: "part",
        expanded: false,
        items: undefined,
        data: selectedTreeItem.document,
        hasReadPermissions: selectedTreeItem.readPermission,
        checked: false,
      },
    ];
  }, [selectedTreeItem]);

  const [state, setState] = useState<ManageStructureState>(initialState);
  const getTree = () =>
    getFullTree(selectedTreeItem.document.id).then((treeItems) => {
      setState({ ...initialState, manageTreeItems: treeItems });
    });

  useEffect(() => {
    if (props.elaborationItem) {
      setState(initialState);
    } else getTree();
  }, [props.elaborationItem]);

  const mapTreeViewDataItemToStructureDocumentPart: (treeViewDataItem: TreeViewDataItem) => StructureDocumentPart = (
    treeViewDataItem
  ) => {
    return {
      name: treeViewDataItem.text,
      id: treeViewDataItem.key,
      subparts: treeViewDataItem.items?.map(mapTreeViewDataItemToStructureDocumentPart),
      driveItemId: treeViewDataItem.driveItemId,
    };
  };

  const onItemDragOver = (event: TreeViewItemDragOverEvent) => {
    dragOverCnt.current++;
    dragClue.current.show(event.pageY + 10, event.pageX, event.item.text, getClueClassName(event));
  };

  const onItemDragEnd = (event: TreeViewItemDragEndEvent) => {
    isDragDrop.current = dragOverCnt.current > 0;
    dragOverCnt.current = 0;
    dragClue.current.hide();

    const eventAnalyzer = new TreeViewDragAnalyzer(event).init();
    if (eventAnalyzer.isDropAllowed && eventAnalyzer.destinationMeta.itemHierarchicalIndex != "0") {
      const updatedTree: any = moveTreeViewItem(
        event.itemHierarchicalIndex,
        state.manageTreeItems,
        eventAnalyzer.getDropOperation() || "child",
        eventAnalyzer.destinationMeta.itemHierarchicalIndex
      );
      if (sameDocumentsUnderSameParent(updatedTree)) {
        NotificationManager.addNotification({
          title: formatMessage("cof.opendocuments.outline.managetreestructure"),
          message: formatMessage("cof.opendocuments.outline.managetreestructure.samedocuments"),
          type: "warning",
          isTemporary: false,
        });
      } else {
        if (state.count === 2) {
          NotificationManager.addNotification({
            title: formatMessage("cof.opendocuments.outline.managetreestructure"),
            message: formatMessage("cof.opendocuments.outline.managetreestructure.changeslimit"),
            type: "warning",
            isTemporary: false,
          });
        }
        setState((s) => ({ ...s, manageTreeItems: updatedTree, count: s.count + 1 }));
        props.updateTreeItems(mapTreeViewDataItemToStructureDocumentPart(updatedTree[0]), true);
      }
    }
  };

  /**
   * Check recurively inside tree items if there are items with the same key under the same parent.
   * @param tree
   */
  const sameDocumentsUnderSameParent = (tree: TreeViewDataItem[]): boolean => {
    let hasDuplicate = false;

    function check(items: TreeViewDataItem[]) {
      if (!items) return;
      const keys = new Set<string>();
      for (const item of items) {
        if (keys.has(item.key)) {
          hasDuplicate = true;
          return;
        }
        keys.add(item.key);
      }
      for (const item of items) {
        if (item.items && item.items.length > 0) {
          check(item.items);
          if (hasDuplicate) return;
        }
      }
    }

    check(tree);
    return hasDuplicate;
  };

  const getClueClassName = (event: any) => {
    const eventAnalyzer = new TreeViewDragAnalyzer(event).init();
    const { itemHierarchicalIndex: itemIndex } = eventAnalyzer.destinationMeta;

    if (eventAnalyzer.isDropAllowed && eventAnalyzer.destinationMeta.itemHierarchicalIndex != "0") {
      switch (eventAnalyzer.getDropOperation()) {
        case "child":
          return "k-i-plus";
        case "before":
          return itemIndex === "0" || itemIndex.endsWith(`${SEPARATOR}0`) ? "k-i-insert-up" : "k-i-insert-middle";
        case "after":
          // eslint-disable-next-line no-case-declarations
          const siblings = getSiblings(itemIndex, rootItem);
          // eslint-disable-next-line no-case-declarations
          const lastIndex = Number(itemIndex.split(SEPARATOR).pop());

          return lastIndex < siblings.length - 1 ? "k-i-insert-middle" : "k-i-insert-down";
        default:
          break;
      }
    }

    return "k-i-cancel";
  };

  function getSiblings(itemIndex: string, data: TreeViewDataItem[]) {
    let result = data;
    const indices = itemIndex.split(SEPARATOR).map((index) => Number(index));
    for (let i = 0; i < indices.length - 1; i++) {
      result = result[indices[i]]?.items || [];
    }

    return result;
  }

  const fetchExpandedNode = (id: string, isReload: boolean, page: number = 0) => {
    if (!id || id.length === 0) return;
    const indexes = id.split("_").map((value) => Number(value)) ?? [];
    if (page !== 0) {
      indexes.pop();
    }
    const { items, item } = copyItemsAndItem(rootItem, indexes);
    item.expanded = isReload ? true : !item.expanded;
    if (!isReload && item.items && page === 0) {
      setState((s) => ({ ...s, treeItems: items }));
      return;
    }
    fetchDocumentPartNodeWithPermissions(item.key, page).then((dataPart) => {
      let childrenDocumentPart: DocumentRoot[] = [];
      dataPart._embedded.content.forEach((part) => childrenDocumentPart.push(part));
      const { items: children, item: parentItem } = copyItemsAndItem(items, indexes);
      if (page !== 0) {
        parentItem.expanded = true;
        let childrens = parentItem.data.subparts as DocumentRoot[];
        parentItem.data.subparts = childrens.concat(childrenDocumentPart);
        parentItem.items.pop();
        parentItem.items = parentItem.items.concat(childrenDocumentPart.map((part) => mapFormToDataItem(part)));
      } else {
        parentItem.data.subparts = childrenDocumentPart;
        parentItem.items = childrenDocumentPart.map((part) => mapFormToDataItem(part));
      }

      if (dataPart.page.totalPages != page + 1) {
        parentItem.items.push({
          text: formatMessage("cof.documentslist.showmore"),
          key: "key",
          hasChildren: false,
          nodeType: "pagination",
          data: { ...dataPart.page, idParent: item.key },
        });
      }

      parentItem.hasChildren = false; // hasChildren is need only for lazy loading
      setState((s) => ({ ...s, treeItems: children }));
    });
  };

  const onExpandChangeManageTree = (event: TreeViewExpandChangeEvent) => {
    fetchExpandedNodeManageTree(event.itemHierarchicalIndex, false);
  };

  const fetchExpandedNodeManageTree = (id: string, isReload: boolean) => {
    if (id && id.length > 0) {
      const indexes = id.split("_").map((value) => Number(value)) ?? [];
      const { items, item } = copyItemsAndItem(state.manageTreeItems, indexes);
      item.expanded = isReload ? true : !item.expanded;
      setState((s) => ({ ...s, manageTreeItems: items }));
    }
  };

  const copyItemsAndItem = (
    items: TreeViewDataItem[],
    indexes: number[]
  ): { items: TreeViewDataItem[]; item: TreeViewDataItem } => {
    const copiedItems = items.slice();
    const item = (copiedItems[indexes[0]] = { ...copiedItems[indexes[0]] });

    if (indexes.length > 1) {
      const { items: children, item: target } = copyItemsAndItem(item.items, indexes.slice(1));
      item.items = children;
      return { items: copiedItems, item: target };
    } else {
      return { items: copiedItems, item: item };
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        marginBottom: "-2.5rem",
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <HeaderPanel data-testid={"header"} applyPadding>
          {formatMessage("cof.opendocuments.outline.managetreestructure") + " - " + props.selectedTreeItem.text}
        </HeaderPanel>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          paddingBottom: "110px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            paddingTop: "12px",
            paddingLeft: "8px",
            paddingRight: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--color-grey)",
              fontSize: "14px",
            }}
          >
            {formatMessage("cof.opendocuments.outline.managetreestructure.info")}
          </div>
          <div
            style={{
              borderBottom: `0.01rem solid ${"var(--color-grey)"}`,
              width: "100%",
              height: "3px",
            }}
          />
          <CustomTreeView
            draggable={true}
            onItemDragOver={state.count < 3 && onItemDragOver}
            onItemDragEnd={state.count < 3 && onItemDragEnd}
            data={processTreeViewItems(state.manageTreeItems, {
              expand: state.expand,
              select: state.selectState,
            })}
            expandIcons={expandIcons}
            onExpandChange={onExpandChangeManageTree}
            size={"small"}
            selectedItemId={props.selectedTreeItem.document.id}
            treeItemRowProps={{
              isDraggable: state.count < 3,
              getIconButtonMenuProps,
              fetchExpandedNode,
            }}
          />
          <TreeViewDragClue ref={dragClue} />
        </div>
      </div>

      <FooterPanel>
        <PrimaryButton
          className={ButtonVariants.primary}
          text={formatMessage("cof.base.save")}
          disabled={!props.modifiedFields}
          onClick={() => {
            props.changeDocumentPartStructure().catch(getTree);
            setState((s) => ({ ...s }));
          }}
          style={{
            float: "right",
          }}
        />
        <PrimaryButton
          className={ButtonVariants.primary}
          text={formatMessage("cof.base.back")}
          onClick={props.onBackButtonClick}
          style={{
            float: "left",
          }}
        />
      </FooterPanel>
    </div>
  );
};
